import { Button, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import React from "react"
import { AppointmentsAvatar, FEATURE_AVATAR_SIZE } from "./AppointmentsAvatar"
import { perViewport, perViewportTypography } from "./responsive"
import type { AppointmentsCompanion } from "./types"
import { Link as ReactRouterLink } from "react-router-dom"

/**
 * These descriptions intentionally duplicate the enterprise Care Team wording. That component
 * lives in the host app and is not safely reusable from this package, so keep the copy aligned when
 * either version changes.
 */
const GENERIC_COMPANION_DESCRIPTION =
  "Carrot Companions are general experts on Carrot, including logistics, choosing a provider, and making the most of your benefit."

const getCompanionDescription = (displayName: string): string =>
  displayName
    ? `${displayName} is a general expert on Carrot, including logistics, choosing a provider, and making the most of your benefit. If ${displayName} is not available, another Carrot Companion will respond.`
    : GENERIC_COMPANION_DESCRIPTION

/**
 * The Carrot Companion block.
 *
 * The companion arrives as a prop rather than from a hook: the host resolves it with
 * `useCarrotCompanion`, which reads the monolithic `carrotClient` and the host's user context, and
 * formats the display name with `formatCarrotCompanionName` — the same helper the care-team surfaces
 * use. Neither is reachable from this package, and reimplementing the name formatting here would
 * risk drifting from the one the rest of the app shows.
 */
export function CarrotCompanionDetails({ companion }: { companion: AppointmentsCompanion | null }): JSX.Element {
  const theme = useTheme()
  const displayName = companion?.displayName.trim() ?? ""
  const hasCompanionIdentity = Boolean(displayName)
  return (
    <Stack sx={{ flexDirection: "column", alignItems: "flex-start" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
      <Typography variant="h3" component="h2">
        Carrot Companion
      </Typography>
      <Stack
        sx={{ flexDirection: "row", justifyContent: "flex-start", alignItems: "center" }}
        spacing={theme.spacing(theme.tokens.spacing.md)}
      >
        {/*
          `.comp img` is 72px and `.mob-comp img` is 64px. This block was passing no `size` at all, so
          it drew at the 54px appointment-row default at every width — a pre-existing gap rather than
          something the mobile pass introduced: CONAPP-263 assigned the 72 to T10 (CONAPP-273), which
          is scoped to strings only, so no ticket had actually applied it. Corrected here because the
          mobile value cannot be set without choosing the desktop one too.
        */}
        {hasCompanionIdentity && (
          <AppointmentsAvatar
            name={companion?.name ?? ""}
            photoUrl={companion?.photoUrl ?? null}
            alt={displayName}
            size={FEATURE_AVATAR_SIZE}
          />
        )}
        {/*
          `.mob-comp-top .mob-btn { width: auto; flex: 0 1 50% }` — half the row on mobile rather than
          the full-width CTA the other blocks get. The `flex-shrink` half also keeps a longer
          translation of this label from pushing the 64px portrait into a horizontal overflow at 320px.
        */}
        <Button
          component={ReactRouterLink}
          to="/messages"
          variant="outlined"
          color="secondary"
          sx={{ flex: perViewport("0 1 50%", "0 1 auto") }}
        >
          Send a message
        </Button>
      </Stack>
      {hasCompanionIdentity && (
        <Typography variant="h6" component="h3">
          {displayName}
        </Typography>
      )}
      {/* `.comp .b` → `.mob-b`, body1 → body2. */}
      <Typography sx={{ typography: perViewportTypography("body2", "body1") }}>
        {getCompanionDescription(displayName)}
      </Typography>
      {/*
        `.comp .d` is italic body1 in `text.secondary`; `.mob-d` steps to italic body2 in
        `text.tertiary` — the only place on the page where frame 9 changes a COLOUR and not just a
        size.

        `&&` rather than a plain `color`, and it is load-bearing. The `typography` key spreads the
        whole role, `color` included, and `Typography` runs its props through MUI's `extendSxProp`,
        which hoists `color` — a system prop — ABOVE that spread. So a top-level `color` here is
        emitted first and then overwritten by the role's own `text.secondary`; verified in the
        emitted CSS, where both breakpoints came out `#2D2D2D`. Doubling the class outranks the
        spread on specificity instead of relying on order, which is the part `extendSxProp` controls.
      */}
      <Typography
        sx={{
          typography: perViewportTypography("body2", "body1"),
          fontStyle: "italic",
          "&&": { color: perViewport(theme.palette.text.tertiary, theme.palette.text.secondary) }
        }}
      >
        Companions cannot answer medical questions.
      </Typography>
    </Stack>
  )
}
